// components/FilterSidebar.jsx
import React from "react";
import classes from "./filterSidebar.module.css";

const FilterSidebar = ({
  priceRange,
  minPrice,
  maxPrice,
  selectedCollection,
  selectedMedium,
  searchQuery,
  location,
  onPriceInputChange,
  onApplyPriceFilter,
  onCollectionChange,
  onMediumChange,
  onSearchChange,
  onLocationChange,
}) => {
  // Collections options
  const collections = [
    "Any",
    "Aboriginal",
    "Abstract",
    "Australiana",
    "Fantasy",
    "Landscape",
    "Animals",
    "Beach & Seascape",
    "Impressionism",
    "Nature",
    "Nude",
    "Outback",
    "People",
    "Photography",
    "Pop",
    "Still Life",
  ];

  // Mediums options
  const mediums = [
    "Any",
    "Acrylic Paint",
    "Oil Paint",
    "Watercolour Paint",
    "Drawings",
    "Mixed Media",
    "Photograph",
    "Paint",
    "Resin",
    "Other Media",
  ];

  // Price range options
  const priceRanges = [
    { label: "Under 500", min: 0, max: 500 },
    { label: "500 - 1000", min: 500, max: 1000 },
    { label: "1000 - 2000", min: 1000, max: 2000 },
    { label: "2000 - 6000", min: 2000, max: 6000 },
    { label: "Over 6000", min: 6000, max: Infinity },
  ];

  // Rating options
  const ratings = [
    { label: "Any rating", value: 0 },
    { label: "★★★★★", value: 5 },
    { label: "★★★★☆", value: 4 },
    { label: "★★★☆☆", value: 3 },
    { label: "★★☆☆☆", value: 2 },
    { label: "★☆☆☆☆", value: 1 },
  ];

  return (
    <div className={classes.sidebar}>
      <div className={classes.filterHeader}>
        <h2>Filter</h2>
        <button className={classes.clearAllBtn}>Clear All</button>
      </div>

      {/* Search */}
      <div className={classes.filterSection}>
        <h3>Search</h3>
        <div className={classes.searchInputContainer}>
          <input
            type="text"
            placeholder="Search by Canvas"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className={classes.searchInput}
          />
          <button className={classes.searchButton}>
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M21 21L16.65 16.65"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Location */}
      <div className={classes.filterSection}>
        <h3>Location</h3>
        <div className={classes.locationInputContainer}>
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={classes.locationIcon}
          >
            <path
              d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <input
            type="text"
            placeholder="Search suburb or town"
            value={location}
            onChange={(e) => onLocationChange(e.target.value)}
            className={classes.locationInput}
          />
        </div>
      </div>

      {/* Pricing */}
      <div className={classes.filterSection}>
        <h3>Pricing starts at</h3>
        <div className={classes.priceRangeButtons}>
          {priceRanges.map((range, index) => (
            <button
              key={index}
              className={`${classes.priceRangeBtn} ${
                priceRange[0] === range.min && priceRange[1] === range.max
                  ? classes.active
                  : ""
              }`}
              onClick={() => {
                onPriceInputChange("min", range.min);
                onPriceInputChange("max", range.max);
                onApplyPriceFilter();
              }}
            >
              {range.label}
            </button>
          ))}
        </div>

        {/* Custom price range */}
        <div className={classes.customPriceRange}>
          <div className={classes.priceInputContainer}>
            <span className={classes.currencySymbol}>$</span>
            <input
              type="number"
              placeholder="1000"
              value={minPrice}
              onChange={(e) => onPriceInputChange("min", e.target.value)}
              className={classes.priceInput}
            />
          </div>
          <span className={classes.priceRangeSeparator}>to</span>
          <div className={classes.priceInputContainer}>
            <span className={classes.currencySymbol}>$</span>
            <input
              type="number"
              placeholder="2000"
              value={maxPrice}
              onChange={(e) => onPriceInputChange("max", e.target.value)}
              className={classes.priceInput}
            />
          </div>
          <button
            className={classes.applyPriceBtn}
            onClick={onApplyPriceFilter}
          >
            →
          </button>
        </div>
      </div>

      {/* Collections */}
      <div className={classes.filterSection}>
        <h3>Collections</h3>
        <div className={classes.optionsGrid}>
          {collections.map((collection, index) => (
            <div key={index} className={classes.optionItem}>
              <input
                type="radio"
                id={`collection-${index}`}
                name="collection"
                checked={selectedCollection === collection}
                onChange={() => onCollectionChange(collection)}
                className={classes.radioInput}
              />
              <label
                htmlFor={`collection-${index}`}
                className={classes.radioLabel}
              >
                {collection}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Mediums */}
      <div className={classes.filterSection}>
        <h3>Mediums</h3>
        <div className={classes.optionsGrid}>
          {mediums.map((medium, index) => (
            <div key={index} className={classes.optionItem}>
              <input
                type="radio"
                id={`medium-${index}`}
                name="medium"
                checked={selectedMedium === medium}
                onChange={() => onMediumChange(medium)}
                className={classes.radioInput}
              />
              <label htmlFor={`medium-${index}`} className={classes.radioLabel}>
                {medium}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Ratings */}
      <div className={classes.filterSection}>
        <h3>Ratings</h3>
        <div className={classes.ratingsContainer}>
          {ratings.map((rating, index) => (
            <div key={index} className={classes.optionItem}>
              <input
                type="radio"
                id={`rating-${index}`}
                name="rating"
                className={classes.radioInput}
              />
              <label htmlFor={`rating-${index}`} className={classes.radioLabel}>
                {rating.label}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Available */}
      <div className={classes.filterSection}>
        <h3>Available</h3>
        <div className={classes.checkboxContainer}>
          <input
            type="checkbox"
            id="include-out-of-stock"
            className={classes.checkboxInput}
          />
          <label
            htmlFor="include-out-of-stock"
            className={classes.checkboxLabel}
          >
            Include Out of Stock
          </label>
        </div>
      </div>
    </div>
  );
};

export default FilterSidebar;
